import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseDialogUploadFileParams = Readonly<{
  acceptedExtensions: string[];
  buttonTextSuccess?: string;
  isMultiple: boolean;
  isOpen: boolean;
  maxSizeBytes: number;
  onChangeFiles?: (files: File[]) => void;
  onSuccess?: (files: File[]) => void;
  title?: string;
}>;

type UseDialogUploadFileResult = Readonly<{
  buttonTextSuccessText: string;
  errorText: string;
  getDeleteFileLabel: (fileName: string) => string;
  handleFilesSelected: (files: File[]) => void;
  handleSuccess: () => void;
  hasFiles: boolean;
  makeHandleRemoveFile: (index: number) => () => void;
  rejectedFiles: string[];
  selectedFiles: File[];
  selectedFilesLabel: string;
  titleText: string;
  uploadingAriaLabel: string;
}>;

function useDialogUploadFile({
  acceptedExtensions,
  buttonTextSuccess,
  isMultiple,
  isOpen,
  maxSizeBytes,
  onChangeFiles,
  onSuccess,
  title,
}: UseDialogUploadFileParams): UseDialogUploadFileResult {
  const { t } = useTranslation(['actions', 'common', 'content']);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const onChangeFilesRef = useRef(onChangeFiles);
  onChangeFilesRef.current = onChangeFiles;

  const normalizedExtensions = acceptedExtensions.map((ext) => ext.toLowerCase());

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setRejectedFiles([]);
    }
  }, [isOpen]);

  useEffect(() => {
    onChangeFilesRef.current?.(selectedFiles);
  }, [selectedFiles]);

  function isValidFile(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return file.size <= maxSizeBytes && normalizedExtensions.includes(ext);
  }

  function handleFilesSelected(files: File[]): void {
    const valid: File[] = [];
    const rejectedNames: string[] = [];
    files.forEach((file) => {
      if (isValidFile(file)) valid.push(file);
      else rejectedNames.push(file.name);
    });

    if (valid.length > 0) {
      if (isMultiple) {
        setSelectedFiles((prev) => [...prev, ...valid]);
      } else {
        setSelectedFiles([valid[valid.length - 1]]);
      }
    }
    setRejectedFiles(rejectedNames);
  }

  function makeHandleRemoveFile(index: number): () => void {
    return function handleRemoveFileAt(): void {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };
  }

  function handleSuccess(): void {
    onSuccess?.(selectedFiles);
  }

  function getDeleteFileLabel(fileName: string): string {
    return t('actions:Delete file', { name: fileName });
  }

  const titleText = title ?? t('common:Upload file');
  const buttonTextSuccessText = buttonTextSuccess ?? t('actions:Add');
  const errorText = t('content:Some files could not be added');
  const selectedFilesLabel = t('common:Selected files');
  const uploadingAriaLabel = t('common:Uploading file');

  return {
    buttonTextSuccessText,
    errorText,
    getDeleteFileLabel,
    handleFilesSelected,
    handleSuccess,
    hasFiles: selectedFiles.length > 0,
    makeHandleRemoveFile,
    rejectedFiles,
    selectedFiles,
    selectedFilesLabel,
    titleText,
    uploadingAriaLabel,
  };
}

export default useDialogUploadFile;
